import { rfiImpact, rfiPriority, rfiStatus } from '@/types/enums';
import {
  ATTACHMENT_MAX_FILE_SIZE_BYTES,
  ATTACHMENT_MAX_FILE_SIZE_LABEL,
} from '@/constants/uploadLimits';
import dayjs from 'dayjs';
import { z } from 'zod';

export const rfiPropsSchema = z.object({
  subject: z.string().nonempty({ error: 'Subject is required' }),
  project: z.coerce.number().min(1, { error: 'Project is required' }),
  release: z.coerce.number().min(1, { error: 'Release is required' }),
  zone_level: z.string().nonempty({ error: 'Zone level is required' }),
  status: z.enum(rfiStatus.map((item) => item.value)).default('open'),
  priority: z.enum(rfiPriority.map((item) => item.value)).default('normal'),
  impact: z.enum(rfiImpact.map((item) => item.value)).default('low'),
  open_date: z
    .string()
    .nonempty({ error: 'Open date is required' })
    .transform((val) => dayjs(val).toDate()),
  due_date: z
    .string()
    .nonempty({ error: 'Due date is required' })
    .transform((val) => dayjs(val).toDate()),
  assigned_users: z.array(
    z.object({
      user: z.coerce.number(),
      role: z.string(),
    }),
  ),
  discipline: z.string(),
  ball_in_court: z.coerce.number(),
  attachments: z
    .array(z.instanceof(File))
    .refine(
      (files) => files.every((f) => f.size <= ATTACHMENT_MAX_FILE_SIZE_BYTES),
      { message: `Each file must be ${ATTACHMENT_MAX_FILE_SIZE_LABEL} or less.` },
    )
    .optional(),
});

const rfiGetSchema = rfiPropsSchema.extend({
  id: z.coerce.number(),
});

export const rfiPatchSchema = rfiPropsSchema.pick({
  zone_level: true,
  priority: true,
  impact: true,
  due_date: true,
  discipline: true,
});

export type Post = z.infer<typeof rfiPropsSchema>;
export type Get = z.infer<typeof rfiGetSchema>;
export type Patch = z.infer<typeof rfiPatchSchema>;

export type RfiMessage = {
  id: number;
  rfi_id: number;
  message: string;
  author: string;
  created_at: string;
  attachments: {
    filename: string;
    file: string;
  }[];
  is_final_answer: string;
};
